import React, { useMemo } from 'react';
import { CheckCircle, Search, X } from 'lucide-react';
import { Sidebar } from '../components/Sidebar';
import { Alert } from '../types';
import { getSeverityColor } from '../utils/helpers';

interface AlertsPageProps {
  currentPage: string;
  activeAlertsCount: number;
  alerts: Alert[];
  alertsLoading: boolean;
  alertsPagination: {
    current_page: number;
    total_pages: number;
    total_items: number;
    per_page: number;
    has_next: boolean;
    has_prev: boolean;
  } | null;
  selectedAlertStatus: string | null;
  selectedAlertPriority: string | null;
  selectedAutoFix: string | null;
  alertSearchQuery: string;
  alertPage: number;
  itemsPerPage: number;
  onNavigate: (page: string) => void;
  onLogout: () => void;
  onSetSelectedAlertStatus: (status: string | null) => void;
  onSetSelectedAlertPriority: (priority: string | null) => void;
  onSetSelectedAutoFix: (autoFix: string | null) => void;
  onSetAlertSearchQuery: (query: string) => void;
  onSetAlertPage: (page: number) => void;
  onSetItemsPerPage: (count: number) => void;
  onAlertClick: (alert: Alert) => void;
}

export const AlertsPage: React.FC<AlertsPageProps> = ({
  currentPage,
  activeAlertsCount,
  alerts,
  alertsLoading,
  alertsPagination,
  selectedAlertStatus,
  selectedAlertPriority,
  selectedAutoFix,
  alertSearchQuery,
  alertPage,
  itemsPerPage,
  onNavigate,
  onLogout,
  onSetSelectedAlertStatus,
  onSetSelectedAlertPriority,
  onSetSelectedAutoFix,
  onSetAlertSearchQuery,
  onSetAlertPage,
  onSetItemsPerPage,
  onAlertClick,
}) => {
  // Fuzzy search function
  const fuzzyMatch = (text: string, query: string): boolean => {
    if (!query) return true;
    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    return lowerText.includes(lowerQuery);
  };

  // Check if query looks like a UUID (alert ID)
  const isUUIDFormat = (str: string): boolean => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
  };

  // Filter and search alerts
  const filteredAlerts = useMemo(() => {
    return alerts.filter((alert) => {
      const isResolved = alert.status === 'RESOLVED';

      // Status filter
      if (selectedAlertStatus === 'ACTIVE' && isResolved) return false;
      if (selectedAlertStatus === 'RESOLVED' && !isResolved) return false;

      // Priority filter
      if (selectedAlertPriority && alert.priority.toString() !== selectedAlertPriority) return false;

      // Auto-fix filter
      if (selectedAutoFix === 'available' && !alert.auto_fix_available) return false;
      if (selectedAutoFix === 'unavailable' && alert.auto_fix_available) return false;

      // Search filter
      if (alertSearchQuery.trim()) {
        const query = alertSearchQuery.trim();

        // If query looks like a UUID, do exact ID match on Foresight alert ID
        if (isUUIDFormat(query)) {
          if (alert.id.toLowerCase() !== query.toLowerCase()) {
            return false;
          }
        } else {
          // Otherwise, fuzzy search on title, summary, and grafana_alert_id
          const matchesTitle = fuzzyMatch(alert.title, query);
          const matchesSummary = fuzzyMatch(alert.annotations.summary || '', query);
          const matchesGrafanaAlertId = fuzzyMatch(alert.grafana_alert_id || '', query);

          if (!matchesTitle && !matchesSummary && !matchesGrafanaAlertId) {
            return false;
          }
        }
      }

      return true;
    }).sort((a, b) => {
      const aResolved = a.status === 'RESOLVED';
      const bResolved = b.status === 'RESOLVED';

      // Active alerts first (false comes before true when sorting boolean)
      if (aResolved !== bResolved) {
        return aResolved ? 1 : -1;
      }

      // Then sort by priority (lower number = higher priority)
      return a.priority - b.priority;
    });
  }, [alerts, selectedAlertStatus, selectedAlertPriority, selectedAutoFix, alertSearchQuery]);
  return (
    <div className="h-screen bg-[#1A1A1A] text-white flex overflow-hidden">
      <Sidebar
        currentPage={currentPage}
        activeAlertsCount={activeAlertsCount}
        onNavigate={onNavigate}
        onLogout={onLogout}
      />
      <div className="flex-1 ml-64 p-8 overflow-y-auto">
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white">Alerts</h1>
            <p className="text-[#8b949e]">Business-Critical Alert Management</p>
          </div>
          {alertsLoading && (
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Loading...
            </span>
          )}
        </div>

        <div className="space-y-6">
          {/* Filter Controls */}
          <div className="bg-[#222222] rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-4 text-white">Filter Alerts</h3>

            {/* Search Bar */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by alert ID, Grafana alert ID, name, or summary..."
                  value={alertSearchQuery}
                  onChange={(e) => {
                    onSetAlertSearchQuery(e.target.value);
                    onSetAlertPage(1);
                  }}
                  className="w-full bg-[#1A1A1A] border border-gray-600 rounded-lg pl-10 pr-10 py-2 text-white text-sm focus:outline-none focus:border-gray-500"
                />
                {alertSearchQuery && (
                  <button
                    onClick={() => {
                      onSetAlertSearchQuery('');
                      onSetAlertPage(1);
                    }}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-4">
              {/* Status Filter */}
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-[#8b949e]">Status:</label>
                <div className="relative">
                  <select
                    value={selectedAlertStatus || 'all'}
                    onChange={(e) => {
                      onSetSelectedAlertStatus(e.target.value === 'all' ? null : e.target.value);
                      onSetAlertPage(1);
                    }}
                    className="bg-[#1A1A1A] border border-gray-600 rounded-lg px-4 py-2 pr-10 text-white text-sm focus:outline-none focus:border-gray-500 appearance-none cursor-pointer min-w-[120px]"
                  >
                    <option value="all">All</option>
                    <option value="active">Active</option>
                    <option value="resolved">Resolved</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Priority Filter */}
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-[#8b949e]">Priority:</label>
                <div className="relative">
                  <select
                    value={selectedAlertPriority || 'all'}
                    onChange={(e) => {
                      onSetSelectedAlertPriority(e.target.value === 'all' ? null : e.target.value);
                      onSetAlertPage(1);
                    }}
                    className="bg-[#1A1A1A] border border-gray-600 rounded-lg px-4 py-2 pr-10 text-white text-sm focus:outline-none focus:border-gray-500 appearance-none cursor-pointer min-w-[120px]"
                  >
                    <option value="all">All</option>
                    <option value="1">Priority 1</option>
                    <option value="2">Priority 2</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Auto-fix Filter */}
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-[#8b949e]">Auto-fix:</label>
                <div className="relative">
                  <select
                    value={selectedAutoFix || 'all'}
                    onChange={(e) => {
                      onSetSelectedAutoFix(e.target.value === 'all' ? null : e.target.value);
                      onSetAlertPage(1);
                    }}
                    className="bg-[#1A1A1A] border border-gray-600 rounded-lg px-4 py-2 pr-10 text-white text-sm focus:outline-none focus:border-gray-500 appearance-none cursor-pointer min-w-[120px]"
                  >
                    <option value="all">All</option>
                    <option value="available">Available</option>
                    <option value="unavailable">Not Available</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Clear Filters */}
              <button
                onClick={() => {
                  onSetSelectedAlertStatus(null);
                  onSetSelectedAlertPriority(null);
                  onSetSelectedAutoFix(null);
                  onSetAlertSearchQuery('');
                  onSetAlertPage(1);
                }}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white text-sm rounded-lg transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>

          <div className="bg-[#222222] rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">All Alerts</h2>
              {alertSearchQuery && (
                <span className="text-sm text-[#8b949e]">
                  Showing {filteredAlerts.length} of {alerts.length} alerts
                </span>
              )}
            </div>

            {/* Combined Alerts Table */}
            <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-300px)] scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
              <table className="w-full min-w-max">
                <thead>
                  <tr className="border-b border-gray-600">
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#8b949e] w-48 whitespace-nowrap">Alert</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#8b949e] w-32 whitespace-nowrap">Alert ID</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#8b949e] w-40 whitespace-nowrap">Grafana Alert ID</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#8b949e] w-20 whitespace-nowrap">Priority</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#8b949e] w-24 whitespace-nowrap">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#8b949e] w-48 whitespace-nowrap">Summary</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAlerts.length > 0 ? (
                    filteredAlerts.map((alert) => {
                      const isResolved = alert.status === 'RESOLVED';
                      return (
                        <tr
                          key={alert.id}
                          className="border-b border-gray-700 hover:bg-[#1A1A1A] cursor-pointer transition-colors"
                          onClick={() => onAlertClick(alert)}
                        >
                          <td className="py-4 px-4">
                            <div className="flex items-center space-x-3">
                              {isResolved ? (
                                <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                              ) : (
                                <div className={`w-3 h-3 rounded-full flex-shrink-0 ${getSeverityColor(alert.priority)}`}></div>
                              )}
                              <div className="font-semibold text-white text-sm">{alert.title}</div>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="text-[#8b949e] text-xs font-mono">{alert.id}</div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="text-[#8b949e] text-xs font-mono">{alert.grafana_alert_id}</div>
                          </td>
                          <td className="py-4 px-4">
                            <span className="text-xs bg-[#222222] px-2 py-1 rounded whitespace-nowrap">Priority {alert.priority}</span>
                          </td>
                          <td className="py-4 px-4">
                            {isResolved ? (
                              <span className="text-xs bg-green-600 px-2 py-1 rounded whitespace-nowrap">Resolved</span>
                            ) : (
                              <span className="text-xs bg-red-600 px-2 py-1 rounded whitespace-nowrap">Active</span>
                            )}
                          </td>
                          <td className="py-4 px-4">
                            <div className="text-[#8b949e] text-sm">{alert.annotations.summary}</div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={6} className="py-8 px-4 text-center text-[#8b949e]">
                        No alerts found matching your search criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {alertsPagination && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-600">
                <div className="flex items-center space-x-4">
                  <div className="text-sm text-[#8b949e]">
                    Showing {((alertsPagination.current_page - 1) * alertsPagination.per_page) + 1}-{Math.min(alertsPagination.current_page * alertsPagination.per_page, alertsPagination.total_items)} of {alertsPagination.total_items} alerts
                  </div>
                  <div className="flex items-center space-x-2">
                    <label className="text-sm text-[#8b949e]">Per page:</label>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        onSetItemsPerPage(Number(e.target.value));
                        onSetAlertPage(1);
                      }}
                      className="bg-[#1A1A1A] border border-gray-600 rounded px-2 py-1 text-white text-sm"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                </div>

                {alertsPagination.total_pages > 1 && (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => onSetAlertPage(1)}
                      disabled={!alertsPagination.has_prev}
                      className="px-3 py-1 text-sm bg-[#1A1A1A] border border-gray-600 rounded text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#2A2A2A]"
                    >
                      First
                    </button>
                    <button
                      onClick={() => onSetAlertPage(alertPage - 1)}
                      disabled={!alertsPagination.has_prev}
                      className="px-3 py-1 text-sm bg-[#1A1A1A] border border-gray-600 rounded text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#2A2A2A]"
                    >
                      Previous
                    </button>

                    <div className="flex items-center space-x-1">
                      {Array.from({ length: Math.min(5, alertsPagination.total_pages) }, (_, i) => {
                        let pageNum: number;
                        if (alertsPagination.total_pages <= 5) {
                          pageNum = i + 1;
                        } else if (alertPage <= 3) {
                          pageNum = i + 1;
                        } else if (alertPage >= alertsPagination.total_pages - 2) {
                          pageNum = alertsPagination.total_pages - 4 + i;
                        } else {
                          pageNum = alertPage - 2 + i;
                        }

                        return (
                          <button
                            key={pageNum}
                            onClick={() => onSetAlertPage(pageNum)}
                            className={`px-3 py-1 text-sm rounded ${
                              alertPage === pageNum
                                ? 'bg-gray-600 text-white border border-gray-500'
                                : 'bg-[#1A1A1A] border border-gray-600 text-white hover:bg-[#2A2A2A]'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => onSetAlertPage(alertPage + 1)}
                      disabled={!alertsPagination.has_next}
                      className="px-3 py-1 text-sm bg-[#1A1A1A] border border-gray-600 rounded text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#2A2A2A]"
                    >
                      Next
                    </button>
                    <button
                      onClick={() => onSetAlertPage(alertsPagination.total_pages)}
                      disabled={!alertsPagination.has_next}
                      className="px-3 py-1 text-sm bg-[#1A1A1A] border border-gray-600 rounded text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#2A2A2A]"
                    >
                      Last
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
